import React, { createContext, useState, useContext, ReactNode } from 'react';
import { CancerType } from '../schema';

export enum CaseInputType {
  TEXTAREA = 1,
  FORM = 2,
  FILE = 3,
}

// Interface for file upload
export interface UploadedFile {
  file: File;
  preview?: string;
}

interface CaseIntakeContextType {
  caseContextText: string;
  caseContextJson: string;
  caseInputType: CaseInputType;
  contextCancerType: CancerType;
  caseContextUploadedFiles?: UploadedFile[];

  setCaseContextText: (text: string) => void;
  setCaseContextJson: (text: string) => void;
  setCaseInputType: (text: CaseInputType) => void;
  setContextCancerType: (cancerType: CancerType) => void;
  setCaseContextUploadedFiles: (files: UploadedFile[]) => void;
  clearCaseContext: () => void;
}

const CaseIntakeContext = createContext<CaseIntakeContextType | undefined>(undefined);

export const CaseIntakeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [caseContextText, setCaseContextText] = useState('');
  const [caseContextJson, setCaseContextJson] = useState('');
  const [caseInputType, setCaseInputType] = useState<CaseInputType>(CaseInputType.TEXTAREA);
  const [contextCancerType, setContextCancerType] = useState<CancerType>(CancerType.UNSPECIFIED_CANCER_TYPE);
  const [caseContextUploadedFiles, setCaseContextUploadedFiles] = useState<UploadedFile[]>([]);

  const clearCaseContext = () => {
    setCaseContextText('');
    setCaseContextJson('');
    setCaseInputType(CaseInputType.TEXTAREA);
    setContextCancerType(CancerType.UNSPECIFIED_CANCER_TYPE);
    setCaseContextUploadedFiles([]);
  };

  return (
    <CaseIntakeContext.Provider value={{ caseContextText, caseContextJson, caseInputType, contextCancerType, setCaseContextText, setCaseContextJson, setCaseInputType, setContextCancerType, clearCaseContext, caseContextUploadedFiles, setCaseContextUploadedFiles }}>
      {children}
    </CaseIntakeContext.Provider>
  );
};

export const useCaseIntake = () => {
  const context = useContext(CaseIntakeContext);
  if (context === undefined) {
    throw new Error('useCaseIntake must be used within a CaseIntakeProvider');
  }
  return context;
};